/**
 * Quote Request / Offerten-System
 * Allows customers to send their cart as a non-binding quote via webhook.
 */

(function () {
  'use strict';

  class QuoteRequestModal {
    constructor() {
      this.modal = document.getElementById('quote-request-modal');
      if (!this.modal) return;

      this.form = document.getElementById('quote-request-form');
      this.itemsContainer = document.getElementById('quote-request-items');
      this.totalsContainer = document.getElementById('quote-request-totals');
      this.successContainer = document.getElementById('quote-request-success');
      this.submitBtn = document.getElementById('quote-request-submit');
      this.debugPanel = document.getElementById('quote-request-debug-panel');

      this.webhookUrl = this.modal.dataset.webhookUrl || '';
      this.successTitle = this.modal.dataset.successTitle || 'Vielen Dank!';
      this.successText = this.modal.dataset.successText || 'Ihre Offertanfrage wurde erfolgreich übermittelt.';
      this.debugMode = this.modal.dataset.debug === 'true';

      this.openedAt = 0;
      this.isOpen = false;
      this.cartData = null;

      // Store initial HTML for reset
      this._initialItemsHTML = this.itemsContainer.innerHTML;
      this._initialTotalsHTML = this.totalsContainer.innerHTML;

      this._bindEvents();
    }

    /* ========================================================================
       Events
       ======================================================================== */

    _bindEvents() {
      // Event delegation for trigger buttons
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quote-request-trigger]');
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          this.open();
        }

        const closeBtn = e.target.closest('[data-action="close-quote-modal"]');
        if (closeBtn) {
          this.close();
        }
      });

      // Close on overlay click (not modal content)
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.close();
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      // Form submit
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._onSubmit();
      });
    }

    /* ========================================================================
       Modal Open / Close
       ======================================================================== */

    async open() {
      if (this.isOpen) return;

      this.openedAt = Date.now();
      this.isOpen = true;

      // Show modal
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('quote-modal-open');

      // Reset to form view (remove success state if present)
      this.successContainer.style.display = 'none';
      this.form.style.display = '';
      this._getCartColumn().style.display = '';
      this._getTitleEl().style.display = '';
      this._getSubtitleEl().style.display = '';

      // Show loading state
      this.itemsContainer.innerHTML =
        '<div class="quote-request-loading">Warenkorb wird geladen...</div>';
      this.totalsContainer.innerHTML = '';

      // Hide debug panel
      if (this.debugPanel) this.debugPanel.style.display = 'none';

      try {
        await this._loadCart();
      } catch (err) {
        console.error('[QuoteRequest] Error loading cart:', err);
        this.itemsContainer.innerHTML =
          '<div class="quote-request-empty"><p>Fehler beim Laden des Warenkorbs.</p></div>';
      }
    }

    close() {
      this.isOpen = false;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('quote-modal-open');

      // Reset form
      this.form.reset();
      this.submitBtn.classList.remove('is-loading');
      this.submitBtn.disabled = false;
    }

    /* ========================================================================
       Cart Loading
       ======================================================================== */

    async _loadCart() {
      // 1. Trigger cart refresh so discount JS re-applies
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );

      // 2. Wait for the mini-cart DOM to settle (rerender + discount JS)
      await this._waitForCartStable();

      // 3. Fetch /cart.js for payload data
      this.cartData = await this._fetchCartJSON();

      // 4. Check if cart is empty
      if (!this.cartData || this.cartData.item_count === 0) {
        this.itemsContainer.innerHTML =
          '<div class="quote-request-empty">' +
          '<p>Dein Warenkorb ist leer.</p>' +
          '<a href="/collections/all">Weiter einkaufen</a>' +
          '</div>';
        this.totalsContainer.innerHTML = '';
        return;
      }

      // 5. Clone cart items from drawer DOM
      this._cloneCartItems();

      // 6. Enrich clones with metafield discounts (needed on static cart page
      //    where applyCartDrawerDiscounts is a no-op)
      this._enrichClonesWithDiscounts();

      // 7. Clone totals
      this._cloneTotals();

      // 8. Debug: show payload preview (no form submit needed)
      if (this.debugMode) {
        try {
          const payload = await this._buildPayload();
          this._showDebugPanel(payload);
        } catch (err) {
          console.warn('[QuoteRequest] Debug payload build failed:', err);
        }
      }
    }

    /**
     * Wait for the #mini-cart DOM to stabilize after cart:refresh.
     * Uses MutationObserver: resolves after 600ms of no changes, max 4s.
     */
    _waitForCartStable() {
      return new Promise((resolve) => {
        const miniCart = document.getElementById('mini-cart');
        if (!miniCart) {
          resolve();
          return;
        }

        let settleTimer = null;
        let resolved = false;

        const done = () => {
          if (resolved) return;
          resolved = true;
          observer.disconnect();
          clearTimeout(maxTimeout);
          resolve();
        };

        const observer = new MutationObserver(() => {
          clearTimeout(settleTimer);
          settleTimer = setTimeout(done, 600);
        });

        observer.observe(miniCart, {
          childList: true,
          subtree: true,
          attributes: true,
          characterData: true
        });

        // Safety timeout: max 4 seconds
        const maxTimeout = setTimeout(done, 4000);

        // Kick-start settle timer in case no mutations happen
        settleTimer = setTimeout(done, 2000);
      });
    }

    async _fetchCartJSON() {
      try {
        const resp = await fetch('/cart.js', { credentials: 'same-origin' });
        return await resp.json();
      } catch (err) {
        console.error('[QuoteRequest] Failed to fetch /cart.js:', err);
        return null;
      }
    }

    /* ========================================================================
       DOM Cloning
       ======================================================================== */

    _cloneCartItems() {
      const miniCart = document.getElementById('mini-cart');
      if (!miniCart) {
        this.itemsContainer.innerHTML = this._initialItemsHTML;
        return;
      }

      const items = miniCart.querySelectorAll('.mini-cart__line-item');
      if (!items.length) {
        this.itemsContainer.innerHTML = this._initialItemsHTML;
        return;
      }

      // Clone and clean items
      const clones = [];
      items.forEach((item) => {
        const clone = item.cloneNode(true);

        // Remove quantity selectors - replace with static quantity display
        const qtySelectorWrap = clone.querySelector('.mini-cart__quantity-selector');
        if (qtySelectorWrap) {
          const qty = item.getAttribute('data-quantity') || '1';
          const qtyEl = document.createElement('span');
          qtyEl.className = 'quote-request-qty';
          qtyEl.textContent = 'Menge: ' + qty;
          qtySelectorWrap.replaceWith(qtyEl);
        }

        // Also handle the separate flex container for qty selector
        const flexWrap = clone.querySelector('div[style*="display: flex"][style*="justify-content: flex-end"]');
        if (flexWrap) {
          const qty = item.getAttribute('data-quantity') || '1';
          const qtyEl = document.createElement('span');
          qtyEl.className = 'quote-request-qty';
          qtyEl.textContent = 'Menge: ' + qty;
          flexWrap.replaceWith(qtyEl);
        }

        // Remove any remove links
        clone.querySelectorAll('.mini-cart__quantity-remove, a[data-action="decrease-quantity"][data-quantity="0"]').forEach(
          (el) => el.remove()
        );

        // Remove quantity-selector buttons/inputs
        clone.querySelectorAll('.quantity-selector').forEach((el) => el.remove());

        // Remove discount pill remove buttons
        clone.querySelectorAll('.discount-pill-remove, [data-action="remove-discount"]').forEach(
          (btn) => btn.remove()
        );

        // Remove upsell elements
        clone.querySelectorAll('.essential-upsell-side-cart-top, .mini-cart__upsell').forEach(
          (el) => el.remove()
        );

        clones.push(clone);
      });

      // Sort by data-sort-priority (ascending: 1, 2, 3, 4, ... 99)
      clones.sort((a, b) => {
        const pa = parseInt(a.getAttribute('data-sort-priority') || '99', 10);
        const pb = parseInt(b.getAttribute('data-sort-priority') || '99', 10);
        return pa - pb;
      });

      this.itemsContainer.innerHTML = '';
      clones.forEach((clone) => this.itemsContainer.appendChild(clone));
    }

    _cloneTotals() {
      const recap = document.querySelector('#mini-cart .mini-cart__recap');
      if (!recap) {
        // Fallback: build from cart.js data
        if (this.cartData) {
          this.totalsContainer.innerHTML =
            '<div class="mini-cart__recap-price-line">' +
            '<span>Zwischensumme</span>' +
            '<span>' + this._formatCHF(this.cartData.total_price) + '</span>' +
            '</div>';
        }
        return;
      }

      this.totalsContainer.innerHTML = '';

      // Clone cart-level discount lines
      const discountLines = recap.querySelectorAll('.mini-cart__recap-price-line--highlight');
      discountLines.forEach((line) => {
        this.totalsContainer.appendChild(line.cloneNode(true));
      });

      // Clone total/subtotal line
      const totalLines = recap.querySelectorAll(
        '.mini-cart__recap-price-line:not(.mini-cart__recap-price-line--highlight)'
      );
      totalLines.forEach((line) => {
        // Skip lines that contain buttons
        if (line.querySelector('button, a.button')) return;
        this.totalsContainer.appendChild(line.cloneNode(true));
      });
    }

    /* ========================================================================
       Discount Enrichment (fallback for static cart page)
       ======================================================================== */

    /**
     * When the modal is opened from the static cart page, applyCartDrawerDiscounts
     * is a no-op (it exits early when isCartPage is true). The cloned #mini-cart
     * items have data-variant-discounts attributes but no JS-applied discount pills
     * or updated prices. This method reads the metafield discount data and applies
     * it to the cloned items.
     */
    _enrichClonesWithDiscounts() {
      // Determine active discount title from localStorage
      let activeDiscountTitle = null;
      try {
        activeDiscountTitle = localStorage.getItem('activeDiscountTitle');
      } catch (e) { /* private browsing */ }

      if (!activeDiscountTitle || activeDiscountTitle === '__no_discount__') return;

      const items = this.itemsContainer.querySelectorAll('.mini-cart__line-item');

      items.forEach((item) => {
        // Skip items that already have discount pills (from cart drawer)
        const existingPill = item.querySelector(
          '.selected-applied-discount, .custom-discount-pill, .discount-pill'
        );
        if (existingPill) return;

        // Read variant discount metafield data
        const discountsAttr = item.getAttribute('data-variant-discounts');
        if (!discountsAttr) return;

        try {
          const discounts = JSON.parse(discountsAttr);
          if (!Array.isArray(discounts) || discounts.length === 0) return;

          // Find the discount matching the active title
          const discount = discounts.find(
            (d) => d.title && d.title.trim() === activeDiscountTitle
          );
          if (!discount) return;

          // Extract percentage (mirrors product-discount-price.js logic)
          let percentage = 0;
          if (discount.percentage !== undefined && discount.percentage !== null) {
            percentage = parseFloat(discount.percentage);
          }

          if (percentage === 0 && discount.value) {
            let val;
            const dv = discount.value;

            if (dv.value !== undefined) {
              val = (typeof dv.value === 'object' && dv.value !== null && dv.value.value !== undefined)
                ? parseFloat(dv.value.value)
                : parseFloat(dv.value);
            } else if (typeof dv === 'number') {
              val = dv;
            } else if (typeof dv === 'string') {
              val = parseFloat(dv);
            }

            if (!isNaN(val) && val > 0) {
              percentage = val <= 1 ? val * 100 : val;
            }
          }

          if (percentage <= 0 || isNaN(percentage)) return;

          // Create discount pill
          const pillHtml =
            '<div class="selected-applied-discount" id="applied-discount-cart-drawer">' +
            '<span><svg width="12" height="12" aria-hidden="true" focusable="false" viewBox="0 0 12 12">' +
            '<path fill-rule="evenodd" clip-rule="evenodd" ' +
            'd="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" ' +
            'fill="currentColor"></path></svg></span>' +
            '<span>' + activeDiscountTitle + '</span>' +
            '</div>';

          const priceList = item.querySelector('.mini-cart__price-list');
          const productInfo = item.querySelector('.mini-cart__product-info');

          if (priceList) {
            priceList.insertAdjacentHTML('afterend', pillHtml);
          } else if (productInfo) {
            productInfo.insertAdjacentHTML('beforeend', pillHtml);
          }

          // Update price display
          const originalPrice = parseInt(item.getAttribute('data-original-price') || '0', 10);
          if (originalPrice > 0) {
            const discountedPrice = Math.round(originalPrice * (1 - percentage / 100));
            const quantity = parseInt(item.getAttribute('data-quantity') || '1', 10);
            const discountedSubtotal = discountedPrice * quantity;

            // Update data attributes for payload
            item.setAttribute('data-discounted-subtotal', String(discountedSubtotal));
            item.setAttribute('data-final-line-price', String(discountedSubtotal));

            // Update price text
            const priceSpan = item.querySelector('.price');
            if (priceSpan) {
              const formattedDiscounted = this._formatCHF(discountedPrice);
              const formattedOriginal = this._formatCHF(originalPrice);
              priceSpan.innerHTML =
                '<s style="opacity:0.5;font-size:0.85em">' + formattedOriginal + '</s> ' +
                '<span class="price--highlight">' + formattedDiscounted + '</span>';
            }
          }
        } catch (e) {
          console.warn('[QuoteRequest] Error enriching clone with discount:', e);
        }
      });
    }

    /* ========================================================================
       Form Submission
       ======================================================================== */

    async _onSubmit() {
      // Anti-bot checks (skipped in debug mode)
      if (!this.debugMode) {
        // Honeypot check
        const honeypot = this.form.querySelector('input[name="company"]');
        if (honeypot && honeypot.value.trim() !== '') {
          this._showSuccess();
          return;
        }

        // Time-based anti-bot (minimum 3 seconds)
        if (Date.now() - this.openedAt < 3000) {
          this._showSuccess();
          return;
        }
      }

      // HTML5 validation
      if (!this.form.reportValidity()) return;

      // Loading state
      this.submitBtn.classList.add('is-loading');
      this.submitBtn.disabled = true;

      try {
        // Refresh cart data
        this.cartData = await this._fetchCartJSON();

        // Build payload
        const payload = await this._buildPayload();

        // Show success
        this._showSuccess();

        // Send webhook in background
        this._sendWebhook(payload);

      } catch (err) {
        console.error('[QuoteRequest] Submit error:', err);
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.disabled = false;
      }
    }

    /* ========================================================================
       Payload Construction
       ======================================================================== */

    async _buildPayload() {
      const formData = new FormData(this.form);
      const cart = this.cartData;

      if (!cart) throw new Error('No cart data');

      // Build items with enriched data
      const items = await this._buildItemsPayload(cart);

      // Calculate totals from DOM-enriched items
      let domTotal = 0;
      const miniCartItems = document.querySelectorAll('#mini-cart .mini-cart__line-item');
      miniCartItems.forEach((el) => {
        const discountedSub = el.getAttribute('data-discounted-subtotal');
        const finalLine = el.getAttribute('data-final-line-price');
        const originalLine = el.getAttribute('data-original-line-price');
        domTotal += parseInt(discountedSub || finalLine || originalLine || '0', 10);
      });

      // Discount extraction
      const discounts = this._extractDiscounts(cart);

      // Totals correction: use higher discount value
      const cartTotalDiscount = cart.total_discount || 0;
      const domDiscount = cart.original_total_price - domTotal;
      const effectiveDiscount = Math.max(cartTotalDiscount, domDiscount > 0 ? domDiscount : 0);
      const effectiveTotal = cart.original_total_price - effectiveDiscount;

      return {
        timestamp: new Date().toISOString(),
        source: window.location.href,
        shop: {
          name: window.Shopify ? window.Shopify.shop : window.location.hostname,
          domain: window.location.hostname,
          currency: window.shop_currency || 'CHF'
        },
        customer: {
          first_name: formData.get('firstname') || '',
          last_name: formData.get('lastname') || '',
          email: formData.get('email') || '',
          phone: formData.get('phone') || '',
          address: {
            street: formData.get('street') || '',
            zip: formData.get('zip') || '',
            city: formData.get('city') || ''
          },
          availability: formData.get('availability') || '',
          remarks: formData.get('remarks') || ''
        },
        cart: {
          token: cart.token || '',
          item_count: cart.item_count,
          original_total_price: cart.original_total_price,
          total_price: effectiveTotal,
          total_discount: effectiveDiscount,
          currency: window.shop_currency || 'CHF',
          items: items
        },
        discounts: discounts,
        formatted: {
          subtotal: this._formatCHF(cart.original_total_price),
          total: this._formatCHF(effectiveTotal),
          total_discount: effectiveDiscount > 0 ? this._formatCHF(effectiveDiscount) : null
        }
      };
    }

    async _buildItemsPayload(cart) {
      const items = [];
      const miniCartItems = document.querySelectorAll('#mini-cart .mini-cart__line-item');

      // Group product handles for barcode batch fetching
      const handlesToFetch = new Set();
      const barcodeCache = {};

      // First pass: collect handles that need barcode fetching
      for (const cartItem of cart.items) {
        const domItem = this._findDOMItem(miniCartItems, cartItem.variant_id);
        const barcode = domItem ? domItem.getAttribute('data-barcode') : '';
        if (!barcode && cartItem.handle) {
          handlesToFetch.add(cartItem.handle);
        }
      }

      // Batch fetch barcodes
      if (handlesToFetch.size > 0) {
        await this._fetchBarcodes(handlesToFetch, barcodeCache);
      }

      // Second pass: build item objects
      for (const cartItem of cart.items) {
        const domItem = this._findDOMItem(miniCartItems, cartItem.variant_id);
        const itemDiscounts = this._extractItemDiscounts(cartItem, domItem);

        // Get barcode
        let barcode = '';
        if (domItem) barcode = domItem.getAttribute('data-barcode') || '';
        if (!barcode && barcodeCache[cartItem.handle]) {
          const cached = barcodeCache[cartItem.handle];
          const variant = cached.find((v) => v.id === cartItem.variant_id);
          if (variant) barcode = variant.barcode || '';
        }

        // Get sort priority from DOM
        const sortPriority = domItem
          ? parseInt(domItem.getAttribute('data-sort-priority') || '99', 10)
          : 99;

        // Get SKU
        const sku = domItem
          ? domItem.getAttribute('data-sku') || cartItem.sku || ''
          : cartItem.sku || '';

        // Get final line price from DOM (may include JS-applied discounts)
        const domFinalLine = domItem
          ? parseInt(domItem.getAttribute('data-discounted-subtotal') ||
                     domItem.getAttribute('data-final-line-price') || '0', 10)
          : 0;
        const finalLinePrice = domFinalLine > 0 ? domFinalLine : cartItem.final_line_price;

        // Calculate discount amount
        const discountAmount = cartItem.original_line_price - finalLinePrice;

        items.push({
          id: cartItem.id,
          key: cartItem.key,
          variant_id: cartItem.variant_id,
          product_id: cartItem.product_id,
          sku: sku,
          barcode: barcode,
          sort_priority: sortPriority,
          title: cartItem.product_title,
          variant_title: cartItem.variant_title,
          product_type: cartItem.product_type,
          vendor: cartItem.vendor,
          handle: cartItem.handle,
          url: cartItem.url,
          image_url: cartItem.image ? 'https:' + cartItem.image : '',
          quantity: cartItem.quantity,
          price: cartItem.price,
          original_price: cartItem.original_price,
          final_price: finalLinePrice / cartItem.quantity,
          line_price: cartItem.line_price,
          original_line_price: cartItem.original_line_price,
          final_line_price: finalLinePrice,
          discount_amount: discountAmount > 0 ? discountAmount : 0,
          discounts: itemDiscounts,
          options: cartItem.options_with_values || [],
          properties: cartItem.properties || {}
        });
      }

      // Sort by sort_priority ascending (1, 2, 3, 4, ... 99)
      items.sort((a, b) => a.sort_priority - b.sort_priority);

      return items;
    }

    _findDOMItem(domItems, variantId) {
      return Array.from(domItems).find(
        (el) => el.getAttribute('data-variant-id') === String(variantId)
      );
    }

    /* ========================================================================
       Barcode Fetching
       ======================================================================== */

    async _fetchBarcodes(handles, cache) {
      const fetchPromises = Array.from(handles).map(async (handle) => {
        try {
          const resp = await fetch('/products/' + handle + '.json');
          if (!resp.ok) return;
          const data = await resp.json();
          if (data.product && data.product.variants) {
            cache[handle] = data.product.variants.map((v) => ({
              id: v.id,
              barcode: v.barcode || ''
            }));
          }
        } catch (err) {
          console.warn('[QuoteRequest] Could not fetch product:', handle, err);
        }
      });

      // Fetch all in parallel with 5s timeout
      await Promise.race([
        Promise.allSettled(fetchPromises),
        new Promise((resolve) => setTimeout(resolve, 5000))
      ]);
    }

    /* ========================================================================
       Discount Extraction
       ======================================================================== */

    /**
     * Extract discount info for a single cart item.
     * Priority: DOM pills > data-discounts-json > cart.js discounts > fallback
     */
    _extractItemDiscounts(cartItem, domItem) {
      const discounts = [];

      if (domItem) {
        // Priority 1: DOM discount pills (JS-applied)
        const pills = domItem.querySelectorAll(
          '.custom-discount-pill, .selected-applied-discount, .discount-pill'
        );
        pills.forEach((pill) => {
          const text = pill.textContent.replace(/[×✕]/g, '').trim();
          if (text) {
            discounts.push({ title: text, amount: 0 });
          }
        });

        // Priority 2: data-discounts-json from Liquid
        if (discounts.length === 0) {
          const jsonStr = domItem.getAttribute('data-variant-discounts');
          if (jsonStr) {
            try {
              const parsed = JSON.parse(jsonStr);
              if (Array.isArray(parsed)) {
                parsed.forEach((d) => {
                  discounts.push({
                    title: d.title || d.name || 'Rabatt',
                    amount: d.amount || d.value || 0
                  });
                });
              }
            } catch (e) {
              // ignore parse errors
            }
          }
        }
      }

      // Priority 3: cart.js line_level_discount_allocations
      if (discounts.length === 0 && cartItem.line_level_discount_allocations) {
        cartItem.line_level_discount_allocations.forEach((alloc) => {
          discounts.push({
            title: alloc.discount_application
              ? alloc.discount_application.title
              : 'Rabatt',
            amount: alloc.amount || 0
          });
        });
      }

      // Priority 4: cart.js discounts array
      if (discounts.length === 0 && cartItem.discounts && cartItem.discounts.length > 0) {
        cartItem.discounts.forEach((d) => {
          discounts.push({
            title: d.title || 'Rabatt',
            amount: d.amount || 0
          });
        });
      }

      // Priority 5: Fallback - calculate from price difference
      if (discounts.length === 0) {
        const diff = cartItem.original_line_price - cartItem.final_line_price;
        if (diff > 0) {
          discounts.push({ title: 'Rabatt', amount: diff });
        }
      }

      return discounts;
    }

    /**
     * Extract cart-level discounts (codes, cart-level, automatic)
     */
    _extractDiscounts(cart) {
      const result = {
        codes: [],
        cart_level: [],
        automatic: []
      };

      // Discount codes
      if (cart.cart_level_discount_applications) {
        cart.cart_level_discount_applications.forEach((d) => {
          result.cart_level.push({
            title: d.title,
            amount: d.total_allocated_amount
          });
        });
      }

      // Line-level discount summaries
      const autoDiscountMap = {};
      if (cart.items) {
        cart.items.forEach((item) => {
          if (item.line_level_discount_allocations) {
            item.line_level_discount_allocations.forEach((alloc) => {
              const title = alloc.discount_application
                ? alloc.discount_application.title
                : 'Rabatt';
              if (!autoDiscountMap[title]) {
                autoDiscountMap[title] = 0;
              }
              autoDiscountMap[title] += alloc.amount || 0;
            });
          }
        });
      }

      Object.entries(autoDiscountMap).forEach(([title, amount]) => {
        result.automatic.push({ title, total_amount: amount });
      });

      return result;
    }

    /* ========================================================================
       Webhook
       ======================================================================== */

    _sendWebhook(payload) {
      if (!this.webhookUrl) {
        console.warn('[QuoteRequest] No webhook URL configured');
        return;
      }

      fetch(this.webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch((err) => {
        console.error('[QuoteRequest] Webhook error:', err);
      });
    }

    /* ========================================================================
       UI Helpers
       ======================================================================== */

    _showSuccess() {
      this.submitBtn.classList.remove('is-loading');
      this.submitBtn.disabled = false;

      // Hide form and cart, show success
      this.form.style.display = 'none';
      this._getCartColumn().style.display = 'none';
      this._getTitleEl().style.display = 'none';
      this._getSubtitleEl().style.display = 'none';

      // Update success texts
      document.getElementById('quote-request-success-title').textContent = this.successTitle;
      document.getElementById('quote-request-success-text').textContent = this.successText;

      this.successContainer.style.display = '';
    }

    _showDebugPanel(payload) {
      if (!this.debugPanel) return;

      this.debugPanel.style.display = '';
      this.debugPanel.innerHTML = '';

      const heading = document.createElement('p');
      heading.style.cssText = 'color:#c29b58;font-weight:600;font-size:12px;margin:0 0 8px;';
      heading.textContent = 'Debug: Payload JSON';
      this.debugPanel.appendChild(heading);

      const pre = document.createElement('pre');
      pre.className = 'quote-request-debug';
      pre.textContent = JSON.stringify(payload, null, 2);
      this.debugPanel.appendChild(pre);

      // Comparison: cart.js vs transformed
      if (this.cartData) {
        const compare = document.createElement('div');
        compare.className = 'quote-request-debug';
        compare.style.marginTop = '8px';

        const cartTotal = this.cartData.total_price;
        const payloadTotal = payload.cart.total_price;
        const diff = cartTotal - payloadTotal;

        compare.innerHTML =
          '<strong>Vergleich:</strong>\n' +
          'cart.js total_price: ' + this._formatCHF(cartTotal) + ' (' + cartTotal + ' Cents)\n' +
          'Payload total_price: ' + this._formatCHF(payloadTotal) + ' (' + payloadTotal + ' Cents)\n' +
          'Differenz: ' + this._formatCHF(Math.abs(diff)) + (diff !== 0 ? ' ⚠️' : ' ✓') + '\n' +
          'cart.js total_discount: ' + this._formatCHF(this.cartData.total_discount) + '\n' +
          'Payload total_discount: ' + this._formatCHF(payload.cart.total_discount);

        this.debugPanel.appendChild(compare);
      }
    }

    _getCartColumn() {
      return this.modal.querySelector('.quote-request-modal__cart');
    }

    _getTitleEl() {
      return this.modal.querySelector('.quote-request-modal__title');
    }

    _getSubtitleEl() {
      return this.modal.querySelector('.quote-request-modal__subtitle') || { style: {} };
    }

    /**
     * Format cents to CHF with Swiss apostrophe thousands separator.
     * e.g., 1425000 -> "CHF 14'250.00"
     */
    _formatCHF(cents) {
      const amount = (parseInt(cents || 0, 10) / 100).toFixed(2);
      const parts = amount.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      return 'CHF ' + parts.join('.');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.quoteRequestModal = new QuoteRequestModal();
    });
  } else {
    window.quoteRequestModal = new QuoteRequestModal();
  }
})();
